import { addDays as dateFnsAddDays, addMonths as dateFnsAddMonths, getYear, isValid, parse } from 'date-fns';
import { format } from 'date-fns/format';
import { parseISO } from 'date-fns/parseISO';

export const formatDate = (dateStr?: string, formatStr?: string) => {
  formatStr = formatStr || 'd MMMM yyyy HH:mm';

  if (!dateStr) return '';
  const parsedDate = parseISO(dateStr);

  if (!isValid(parsedDate)) return '';

  return format(parsedDate, formatStr);
};

export const formatDateOnly = (dateStr?: string) => formatDate(dateStr, 'd MMMM yyyy');

export const formatDob = (dobStr?: string, formatStr: string = 'd MMMM yyyy') => {
  if (!dobStr) return '';
  const parsed = parse(dobStr, 'dd-MM-yyyy', new Date());

  if (!isValid(parsed)) return '';

  return format(parsed, formatStr);
};

export const toIsoDate = (date: Date): string => format(date, 'yyyy-MM-dd');

export const fromIsoDate = (isoDate: string): Date => parseISO(isoDate);

export const getCurrentDate = () => toIsoDate(new Date());

export const toDateTimeLocal = (isoString: string): string => {
  if (!isoString) {
    return '';
  }

  const date = new Date(isoString);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const pad = (value: number) => value.toString().padStart(2, '0');

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
    date.getMinutes(),
  )}`;
};

export const getCurrentTime = (isoString?: string): string => {
  if (isoString) {
    return toDateTimeLocal(isoString);
  }

  const now = new Date();

  return format(now, "yyyy-MM-dd'T'HH:mm");
};

export const getTodayIsoDate = (isoString?: string): string => {
  if (isoString) {
    const date = new Date(isoString);

    if (!Number.isNaN(date.getTime())) {
      return toIsoDate(date);
    }
  }

  return getCurrentDate();
};

export const addDays = (isoString: string, days: number): string => {
  const date = new Date(isoString);

  if (Number.isNaN(date.getTime())) {
    return isoString;
  }

  return dateFnsAddDays(date, days).toISOString();
};

export const addDaysToDateTimeLocal = (dateTimeLocal: string, days: number): string => {
  const date = new Date(dateTimeLocal);

  if (Number.isNaN(date.getTime())) {
    return dateTimeLocal;
  }

  const newDate = dateFnsAddDays(date, days);

  return toDateTimeLocal(newDate.toISOString());
};

export const addMillisecondsToDateTimeLocal = (dateTimeLocal: string, ms: number): string => {
  const isoString = `${dateTimeLocal}:00.000Z`;
  const date = new Date(isoString);

  if (Number.isNaN(date.getTime())) {
    return dateTimeLocal;
  }

  const newDate = new Date(date.getTime() + ms);

  return toDateTimeLocal(newDate.toISOString());
};

export const isValidDobDate = (
  dateStr: string,
  minYear: number = 1900,
  maxYear: number = new Date().getFullYear(),
): boolean => {
  const date = parse(dateStr, 'dd-MM-yyyy', new Date());
  const year = getYear(date);

  return isValid(date) && year >= minYear && year <= maxYear;
};

export const toDobIso = (dob: string) => {
  const [day, month, year] = dob.split('-');

  return `${year}-${month}-${day}`;
};

export const getDaySuffix = (day: number) => {
  if (day > 3 && day < 21) return 'th';

  return ['th', 'st', 'nd', 'rd'][day % 10] ?? 'th';
};

export const dayWithSuffix = (day: number) => {
  return `${day}${getDaySuffix(day)}`;
};

export const addMonths = (date: Date, months: number): Date => {
  return dateFnsAddMonths(date, months);
};

export const setDayOfMonth = (date: Date, day: number): Date => {
  const newDate = new Date(date);
  newDate.setDate(day);

  if (newDate.getDate() !== day) {
    newDate.setDate(0);
  }

  return newDate;
};
